import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { submitLeadFromLanding } from '@/lib/services/leads';
import { trackLeadSubmission } from '@/lib/analytics/events';
import type { LeadLandingPayload } from '@/lib/types/lead';
import type { LeadFormContent } from '@/lib/types/siteSettings';

interface LeadFormProps {
  campaignSource: string | null;
  campaignMedium: string | null;
  desiredPropertyId: string | null;
  desiredUnitId: string | null;
  formContent: LeadFormContent;
  onSuccess?: (leadId: string) => void;
}

const INITIAL_FORM: LeadLandingPayload = {
  fullName: '',
  email: '',
  phone: null,
  campaignSource: null,
  campaignMedium: null,
  desiredPropertyId: null,
  desiredUnitId: null,
  budgetMonthly: null,
  preferredMoveInDate: null,
  notes: null,
};

const LEAD_PROFILE_KEY = 'public_lead_profile';

export const DEFAULT_LEAD_FORM_CONTENT: LeadFormContent = {
  fullNameLabel: 'Full name',
  emailLabel: 'Work email',
  phoneLabel: 'Phone number (optional)',
  budgetLabel: 'Monthly budget (CAD)',
  moveInLabel: 'Target move-in date',
  notesLabel: 'Tell us more about your needs',
  submitLabel: 'Submit request',
  consentText:
    'By submitting this form you consent to Strashin Developments contacting you about leasing opportunities. You can opt out at any time.',
  successToast: 'Lead submitted. Our team will be in touch shortly.',
  errorToast: 'We could not submit your request. Please try again.',
};

const resolveText = (value: string, fallback: string) =>
  value.trim().length > 0 ? value : fallback;

function loadLeadProfile(): Pick<LeadLandingPayload, 'fullName' | 'email' | 'phone'> {
  if (typeof window === 'undefined') {
    return { fullName: '', email: '', phone: null };
  }
  try {
    const stored = window.localStorage.getItem(LEAD_PROFILE_KEY);
    if (!stored) {
      return { fullName: '', email: '', phone: null };
    }
    const parsed = JSON.parse(stored) as Partial<LeadLandingPayload>;
    return {
      fullName: typeof parsed.fullName === 'string' ? parsed.fullName : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
      phone: typeof parsed.phone === 'string' ? parsed.phone : null,
    };
  } catch {
    return { fullName: '', email: '', phone: null };
  }
}

function persistLeadProfile(values: LeadLandingPayload): void {
  if (typeof window === 'undefined') {
    return;
  }
  const payload = {
    fullName: values.fullName,
    email: values.email,
    phone: values.phone,
  };
  window.localStorage.setItem(LEAD_PROFILE_KEY, JSON.stringify(payload));
}

export function LeadForm({
  campaignSource,
  campaignMedium,
  desiredPropertyId,
  desiredUnitId,
  formContent,
  onSuccess,
}: LeadFormProps): JSX.Element {
  const [formState, setFormState] = useState<LeadLandingPayload>({
    ...INITIAL_FORM,
    ...loadLeadProfile(),
    campaignSource,
    campaignMedium,
    desiredPropertyId,
    desiredUnitId,
  });
  const resolvedContent = useMemo(
    () => ({
      fullNameLabel: resolveText(
        formContent.fullNameLabel,
        DEFAULT_LEAD_FORM_CONTENT.fullNameLabel,
      ),
      emailLabel: resolveText(formContent.emailLabel, DEFAULT_LEAD_FORM_CONTENT.emailLabel),
      phoneLabel: resolveText(formContent.phoneLabel, DEFAULT_LEAD_FORM_CONTENT.phoneLabel),
      budgetLabel: resolveText(formContent.budgetLabel, DEFAULT_LEAD_FORM_CONTENT.budgetLabel),
      moveInLabel: resolveText(formContent.moveInLabel, DEFAULT_LEAD_FORM_CONTENT.moveInLabel),
      notesLabel: resolveText(formContent.notesLabel, DEFAULT_LEAD_FORM_CONTENT.notesLabel),
      submitLabel: resolveText(formContent.submitLabel, DEFAULT_LEAD_FORM_CONTENT.submitLabel),
      consentText: resolveText(formContent.consentText, DEFAULT_LEAD_FORM_CONTENT.consentText),
      successToast: resolveText(formContent.successToast, DEFAULT_LEAD_FORM_CONTENT.successToast),
      errorToast: resolveText(formContent.errorToast, DEFAULT_LEAD_FORM_CONTENT.errorToast),
    }),
    [formContent],
  );

  const mutation = useMutation({
    mutationFn: async (values: LeadLandingPayload) => submitLeadFromLanding({ values }),
  });

  const isSubmitting = mutation.isPending;

  useEffect(() => {
    persistLeadProfile(formState);
  }, [formState]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    if (!event.currentTarget.reportValidity()) {
      return;
    }
    event.preventDefault();

    try {
      const result = await mutation.mutateAsync(formState);
      toast.success(resolvedContent.successToast);
      trackLeadSubmission({
        leadId: result.id,
        campaignSource: formState.campaignSource,
        campaignMedium: formState.campaignMedium,
        stage: 'new',
      });
      onSuccess?.(result.id);
    } catch (error) {
      toast.error(resolvedContent.errorToast);
      if (error instanceof Error) {
        console.error('Lead submission failed', error.message);
      } else {
        console.error('Lead submission failed', error);
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-3xl bg-white p-6 shadow-2xl md:p-8"
    >
      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-text-primary" htmlFor="lead-full-name">
          {resolvedContent.fullNameLabel}
        </label>
        <input
          id="lead-full-name"
          name="fullName"
          type="text"
          required
          autoComplete="name"
          className="rounded-xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
          value={formState.fullName}
          onChange={(event) => {
            setFormState((current) => ({ ...current, fullName: event.target.value }));
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-text-primary" htmlFor="lead-email">
          {resolvedContent.emailLabel}
        </label>
        <input
          id="lead-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          className="rounded-xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
          value={formState.email}
          onChange={(event) => {
            setFormState((current) => ({ ...current, email: event.target.value }));
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-text-primary" htmlFor="lead-phone">
          {resolvedContent.phoneLabel}
        </label>
        <input
          id="lead-phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          className="rounded-xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
          value={formState.phone ?? ''}
          onChange={(event) => {
            const value = event.target.value;
            setFormState((current) => ({ ...current, phone: value.length > 0 ? value : null }));
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-text-primary" htmlFor="lead-budget">
          {resolvedContent.budgetLabel}
        </label>
        <input
          id="lead-budget"
          name="budgetMonthly"
          type="number"
          min={0}
          className="rounded-xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
          value={formState.budgetMonthly ?? ''}
          onChange={(event) => {
            const value = event.target.value;
            setFormState((current) => ({
              ...current,
              budgetMonthly: value.length > 0 ? Number.parseInt(value, 10) : null,
            }));
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-text-primary" htmlFor="lead-move-in">
          {resolvedContent.moveInLabel}
        </label>
        <input
          id="lead-move-in"
          name="preferredMoveInDate"
          type="date"
          className="rounded-xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
          value={formState.preferredMoveInDate ?? ''}
          onChange={(event) => {
            const value = event.target.value;
            setFormState((current) => ({
              ...current,
              preferredMoveInDate: value.length > 0 ? value : null,
            }));
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-text-primary" htmlFor="lead-notes">
          {resolvedContent.notesLabel}
        </label>
        <textarea
          id="lead-notes"
          name="notes"
          rows={4}
          className="rounded-xl border border-surface-subtle px-4 py-3 text-base text-text-primary shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm"
          value={formState.notes ?? ''}
          onChange={(event) => {
            const value = event.target.value;
            setFormState((current) => ({ ...current, notes: value.length > 0 ? value : null }));
          }}
        />
      </div>

      <button
        type="submit"
        className="mt-2 inline-flex items-center justify-center rounded-full bg-brand-primary px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Submitting…' : resolvedContent.submitLabel}
      </button>
      <p className="text-xs text-text-primary/60">{resolvedContent.consentText}</p>
    </form>
  );
}
